import * as React from "react";
import { Snackbar } from "Components/Base/Feedback/Snackbar";
import { Alert } from "Components/Base/Feedback/Alert";

/**
 * A customizable snackbar alert component built with MUI.
 * Combines MUI's Snackbar with Alert component for notifications.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls the visibility of the snackbar
 * @param {('error'|'warning'|'info'|'success')} [props.severity='info'] - Defines the severity level of the alert
 * @param {string} props.message - The message to display in the alert
 * @param {Function} props.onClose - Callback function triggered when the snackbar closes
 * @param {Object} [props.anchorOrigin={ vertical: 'bottom', horizontal: 'left' }] - Position of the snackbar on screen
 * @param {number} [props.autoHideDuration=10000] - Time in milliseconds before the snackbar auto-hides
 * @param {('standard'|'filled'|'outlined')} [props.variant='standard'] - Visual style variant of the alert
 * @param {('small'|'medium'|'large')} [props.size='medium'] - Size of the alert component
 * @returns {React.ReactElement} A snackbar alert component
 *
 * @example
 * <SnackbarAlert
 *   open={this.state.open}
 *   message="Operation successful!"
 *   severity="success"
 *   onClose={() => this.setState({ open: false })}
 * />
 */
const SnackbarAlert = ({
  open,
  severity = "info",
  message,
  onClose,
  anchorOrigin,
  autoHideDuration = 5000,
  variant = "standard",
  size = "medium",
}) => {
  if (!open) {
    return null;
  }
  return (
    <Snackbar
      open={open}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      autoHideDuration={autoHideDuration}
    >
      <Alert
        severity={severity}
        variant={variant}
        onClose={onClose}
        message={message}
        size={size}
      />
    </Snackbar>
  );
};

export default SnackbarAlert;
